'use client';

import { useState, useEffect, useCallback } from 'react';
import ProjectCard from './ProjectCard';
import FilterBar, { FilterState } from './FilterBar';
import StatsBar from './StatsBar';
import TestimonialCard from './TestimonialCard';
import CTASection from './CTASection';

interface Technology {
  name: string;
  category: string;
}

interface ProjectMetrics {
  label: string;
  value: string;
}

interface Project {
  id: string;
  title: string;
  category: string;
  industry: string;
  description: string;
  image: string;
  alt: string;
  technologies: Technology[];
  metrics: ProjectMetrics[];
  liveUrl: string;
  featured: boolean;
  completionDate: string;
}

interface Testimonial {
  id: string;
  projectId: string;
  name: string;
  role: string;
  company: string;
  image: string;
  alt: string;
  testimonial: string;
  rating: number;
  projectTitle: string;
}

interface FilterOption {
  label: string;
  value: string;
}

interface PortfolioInteractiveProps {
  projects: Project[];
  testimonials: Testimonial[];
  categories: FilterOption[];
  industries: FilterOption[];
  technologies: FilterOption[];
}

const PortfolioInteractive = ({
  projects,
  testimonials,
  categories,
  industries,
  technologies
}: PortfolioInteractiveProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [filteredProjects, setFilteredProjects] = useState<Project[]>(projects);
  const [activeFilters, setActiveFilters] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleFilterChange = useCallback((filters: FilterState) => {
    let filtered = [...projects];
    let filterCount = 0;

    if (filters.category !== 'all') {
      filtered = filtered.filter(p => p.category === filters.category);
      filterCount++;
    }

    if (filters.industry !== 'all') {
      filtered = filtered.filter(p => p.industry === filters.industry);
      filterCount++;
    }

    if (filters.technology !== 'all') {
      filtered = filtered.filter(p =>
        p.technologies.some(t => t.name === filters.technology)
      );
      filterCount++;
    }

    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        p.title.toLowerCase().includes(query) ||
        p.description.toLowerCase().includes(query)
      );
      filterCount++;
    }

    switch (filters.sortBy) {
      case 'featured':
        filtered.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
        break;
      case 'recent':
        filtered.sort((a, b) => new Date(b.completionDate).getTime() - new Date(a.completionDate).getTime());
        break;
      case 'oldest':
        filtered.sort((a, b) => new Date(a.completionDate).getTime() - new Date(b.completionDate).getTime());
        break;
      case 'alphabetical':
        filtered.sort((a, b) => a.title.localeCompare(b.title));
        break;
    }

    setFilteredProjects(filtered);
    setActiveFilters(filterCount);
  }, [projects]);

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/portfolio-gallery/components/PortfolioInteractive.tsx:127:6" data-component-path="src/app/portfolio-gallery/components/PortfolioInteractive.tsx" data-component-line="127" data-component-file="PortfolioInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <div data-component-id="src/app/portfolio-gallery/components/PortfolioInteractive.tsx:128:8" data-component-path="src/app/portfolio-gallery/components/PortfolioInteractive.tsx" data-component-line="128" data-component-file="PortfolioInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-20%20bg-muted%20animate-pulse%22%7D" className="h-20 bg-muted animate-pulse" />
        <div data-component-id="src/app/portfolio-gallery/components/PortfolioInteractive.tsx:129:8" data-component-path="src/app/portfolio-gallery/components/PortfolioInteractive.tsx" data-component-line="129" data-component-file="PortfolioInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-6%20lg%3Apx-8%20py-12%22%7D" className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
          <div data-component-id="src/app/portfolio-gallery/components/PortfolioInteractive.tsx:130:10" data-component-path="src/app/portfolio-gallery/components/PortfolioInteractive.tsx" data-component-line="130" data-component-file="PortfolioInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-8%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, i) => (
              <div data-component-id="src/app/portfolio-gallery/components/PortfolioInteractive.tsx:132:14" data-component-path="src/app/portfolio-gallery/components/PortfolioInteractive.tsx" data-component-line="132" data-component-file="PortfolioInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-muted%20animate-pulse%20rounded-lg%22%7D" key={i} className="h-96 bg-muted animate-pulse rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-component-id="src/app/portfolio-gallery/components/PortfolioInteractive.tsx:141:4" data-component-path="src/app/portfolio-gallery/components/PortfolioInteractive.tsx" data-component-line="141" data-component-file="PortfolioInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      <FilterBar data-component-id="src/app/portfolio-gallery/components/PortfolioInteractive.tsx:142:6" data-component-path="src/app/portfolio-gallery/components/PortfolioInteractive.tsx" data-component-line="142" data-component-file="PortfolioInteractive.tsx" data-component-name="FilterBar" data-component-content="%7B%22elementName%22%3A%22FilterBar%22%7D"
        categories={categories}
        industries={industries}
        technologies={technologies}
        onFilterChange={handleFilterChange}
      />

      <StatsBar data-component-id="src/app/portfolio-gallery/components/PortfolioInteractive.tsx:149:6" data-component-path="src/app/portfolio-gallery/components/PortfolioInteractive.tsx" data-component-line="149" data-component-file="PortfolioInteractive.tsx" data-component-name="StatsBar" data-component-content="%7B%22elementName%22%3A%22StatsBar%22%7D"
        totalProjects={projects.length}
        filteredCount={filteredProjects.length}
        activeFilters={activeFilters}
      />

      <section data-component-id="src/app/portfolio-gallery/components/PortfolioInteractive.tsx:155:6" data-component-path="src/app/portfolio-gallery/components/PortfolioInteractive.tsx" data-component-line="155" data-component-file="PortfolioInteractive.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22py-12%20lg%3Apy-16%22%7D" className="py-12 lg:py-16">
        <div data-component-id="src/app/portfolio-gallery/components/PortfolioInteractive.tsx:156:8" data-component-path="src/app/portfolio-gallery/components/PortfolioInteractive.tsx" data-component-line="156" data-component-file="PortfolioInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-6%20lg%3Apx-8%22%7D" className="max-w-7xl mx-auto px-6 lg:px-8">
          {filteredProjects.length > 0 ? (
            <div data-component-id="src/app/portfolio-gallery/components/PortfolioInteractive.tsx:158:12" data-component-path="src/app/portfolio-gallery/components/PortfolioInteractive.tsx" data-component-line="158" data-component-file="PortfolioInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-8%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProjects.map((project) => (
                <ProjectCard data-component-id="src/app/portfolio-gallery/components/PortfolioInteractive.tsx:160:16" data-component-path="src/app/portfolio-gallery/components/PortfolioInteractive.tsx" data-component-line="160" data-component-file="PortfolioInteractive.tsx" data-component-name="ProjectCard" data-component-content="%7B%22elementName%22%3A%22ProjectCard%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" key={project.id} {...project} />
              ))}
            </div>
          ) : (
            <div data-component-id="src/app/portfolio-gallery/components/PortfolioInteractive.tsx:164:12" data-component-path="src/app/portfolio-gallery/components/PortfolioInteractive.tsx" data-component-line="164" data-component-file="PortfolioInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20py-16%22%7D" className="text-center py-16">
              <p data-component-id="src/app/portfolio-gallery/components/PortfolioInteractive.tsx:165:14" data-component-path="src/app/portfolio-gallery/components/PortfolioInteractive.tsx" data-component-line="165" data-component-file="PortfolioInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%20text-text-secondary%20mb-4%22%2C%22textContent%22%3A%22No%20projects%20found%20matching%20your%20filters.%22%7D" className="text-lg text-text-secondary mb-4">
                No projects found matching your filters.
              </p>
              <p data-component-id="src/app/portfolio-gallery/components/PortfolioInteractive.tsx:168:14" data-component-path="src/app/portfolio-gallery/components/PortfolioInteractive.tsx" data-component-line="168" data-component-file="PortfolioInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-text-secondary%22%2C%22textContent%22%3A%22Try%20adjusting%20your%20search%20criteria%20or%20reset%20filters.%22%7D" className="text-sm text-text-secondary">
                Try adjusting your search criteria or reset filters.
              </p>
            </div>
          )}
        </div>
      </section>

      <section data-component-id="src/app/portfolio-gallery/components/PortfolioInteractive.tsx:176:6" data-component-path="src/app/portfolio-gallery/components/PortfolioInteractive.tsx" data-component-line="176" data-component-file="PortfolioInteractive.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22py-12%20lg%3Apy-16%20bg-surface%22%7D" className="py-12 lg:py-16 bg-surface">
        <div data-component-id="src/app/portfolio-gallery/components/PortfolioInteractive.tsx:177:8" data-component-path="src/app/portfolio-gallery/components/PortfolioInteractive.tsx" data-component-line="177" data-component-file="PortfolioInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-6%20lg%3Apx-8%22%7D" className="max-w-7xl mx-auto px-6 lg:px-8">
          <div data-component-id="src/app/portfolio-gallery/components/PortfolioInteractive.tsx:178:10" data-component-path="src/app/portfolio-gallery/components/PortfolioInteractive.tsx" data-component-line="178" data-component-file="PortfolioInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-12%22%7D" className="text-center mb-12">
            <h2 data-component-id="src/app/portfolio-gallery/components/PortfolioInteractive.tsx:179:12" data-component-path="src/app/portfolio-gallery/components/PortfolioInteractive.tsx" data-component-line="179" data-component-file="PortfolioInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-3xl%20lg%3Atext-4xl%20font-bold%20text-text-primary%20mb-4%22%2C%22textContent%22%3A%22Client%20Success%20Stories%22%7D" className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
              Client Success Stories
            </h2>
            <p data-component-id="src/app/portfolio-gallery/components/PortfolioInteractive.tsx:182:12" data-component-path="src/app/portfolio-gallery/components/PortfolioInteractive.tsx" data-component-line="182" data-component-file="PortfolioInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%20text-text-secondary%20max-w-2xl%20mx-auto%22%2C%22textContent%22%3A%22Hear%20what%20our%20clients%20have%20to%20say%20about%20their%20experience%20working%20with%20vidhm%20Solutions%22%7D" className="text-lg text-text-secondary max-w-2xl mx-auto">
              Hear what our clients have to say about their experience working with vidhm Solutions
            </p>
          </div>

          <div data-component-id="src/app/portfolio-gallery/components/PortfolioInteractive.tsx:187:10" data-component-path="src/app/portfolio-gallery/components/PortfolioInteractive.tsx" data-component-line="187" data-component-file="PortfolioInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-8%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <TestimonialCard data-component-id="src/app/portfolio-gallery/components/PortfolioInteractive.tsx:189:14" data-component-path="src/app/portfolio-gallery/components/PortfolioInteractive.tsx" data-component-line="189" data-component-file="PortfolioInteractive.tsx" data-component-name="TestimonialCard" data-component-content="%7B%22elementName%22%3A%22TestimonialCard%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" key={testimonial.id} {...testimonial} />
            ))}
          </div>
        </div>
      </section>

      <CTASection data-component-id="src/app/portfolio-gallery/components/PortfolioInteractive.tsx:195:6" data-component-path="src/app/portfolio-gallery/components/PortfolioInteractive.tsx" data-component-line="195" data-component-file="PortfolioInteractive.tsx" data-component-name="CTASection" data-component-content="%7B%22elementName%22%3A%22CTASection%22%7D" />
    </div>
  );
};

export default PortfolioInteractive;